import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { uploadPublicImage } from "@/lib/upload";
import { usePortalUserContext } from "@/admin/PortalUserContext";
import { postgrestErrorMessage } from "@/lib/postgrestErrorMessage";
import { AdminImagePicker } from "@/components/admin/AdminImagePicker";
import { adminListRowHighlightClass } from "@/lib/adminListRowHighlight";
import {
  adminAddNewSecondaryButtonClass,
  adminFormSectionClass,
  adminListSectionClass,
} from "@/lib/adminSectionChrome";
import {
  lockedScopeFromAssignment,
  pickActiveScopedAssignment,
  portalHasSiteWideWrite,
} from "@/lib/portalSession";
type ParentKind = "branch" | "batch" | "association" | "oba";
type Opt = { id: string; label: string };

type ProjectRow = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
  sort_order: number | null;
  display_date: string | null;
  created_at?: string;
  updated_at?: string;
  branch_id: string | null;
  batch_id: string | null;
  association_id: string | null;
};

type DirOwner = "all" | "oba" | "branch" | "batch" | "association";

function escapeIlikePattern(s: string) {
  return s.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

export function ProjectsTab() {
  const portal = usePortalUserContext();
  const siteWide = portal ? portalHasSiteWideWrite(portal.grants) : false;
  const activeScoped = portal
    ? pickActiveScopedAssignment(portal.assignments, portal.activeAssignmentId)
    : null;
  const lockedScope = portal && !siteWide ? lockedScopeFromAssignment(activeScoped) : null;
  const lockedKind: ParentKind | null = lockedScope?.lockedKind ?? null;
  const lockedParentId = lockedScope?.lockedParentId ?? null;

  const [kind, setKind] = useState<ParentKind>(lockedKind ?? "branch");
  const [parents, setParents] = useState<Opt[]>([]);
  const [parentId, setParentId] = useState(lockedParentId ?? "");
  const [projects, setProjects] = useState<ProjectRow[]>([]);

  const [dirOwner, setDirOwner] = useState<DirOwner>("all");
  const [dirParentId, setDirParentId] = useState("");
  const [dirParents, setDirParents] = useState<Opt[]>([]);
  const [dirSearch, setDirSearch] = useState("");
  const [directoryProjects, setDirectoryProjects] = useState<ProjectRow[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [formNotice, setFormNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [listNotice, setListNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fkField = useMemo(() => {
    if (kind === "branch") return "branch_id";
    if (kind === "batch") return "batch_id";
    if (kind === "association") return "association_id";
    return null;
  }, [kind]);

  useEffect(() => {
    if (lockedKind) setKind(lockedKind);
    if (lockedParentId) setParentId(lockedParentId);
  }, [lockedKind, lockedParentId]);

  const loadParents = useCallback(async () => {
    if (kind === "oba") {
      setParents([]);
      return;
    }
    if (!lockedParentId) setParentId("");
    if (kind === "branch") {
      const { data } = await supabase.from("branches").select("id,name").order("name");
      const list = (data ?? []).map((r: { id: string; name: string }) => ({ id: r.id, label: r.name }));
      setParents(list);
      if (lockedParentId && lockedKind === "branch") setParentId(lockedParentId);
    } else if (kind === "batch") {
      const { data } = await supabase
        .from("batches")
        .select("id,custom_title,official_title,slug")
        .order("al_year", { ascending: false, nullsFirst: false });
      const list = (data ?? []).map(
        (r: { id: string; custom_title: string | null; official_title: string | null; slug: string }) => ({
          id: r.id,
          label: r.custom_title || r.official_title || r.slug,
        })
      );
      setParents(list);
      if (lockedParentId && lockedKind === "batch") setParentId(lockedParentId);
    } else {
      const { data } = await supabase.from("associations").select("id,name").order("name");
      const list = (data ?? []).map((r: { id: string; name: string }) => ({ id: r.id, label: r.name }));
      setParents(list);
      if (lockedParentId && lockedKind === "association") setParentId(lockedParentId);
    }
  }, [kind, lockedKind, lockedParentId]);

  const loadProjects = useCallback(async () => {
    if (kind === "oba") {
      const { data } = await supabase
        .from("projects")
        .select("*")
        .is("branch_id", null)
        .is("batch_id", null)
        .is("association_id", null)
        .order("updated_at", { ascending: false });
      setProjects((data as ProjectRow[]) ?? []);
      return;
    }
    if (!parentId) {
      setProjects([]);
      return;
    }
    let q = supabase
      .from("projects")
      .select("*")
      .order("updated_at", { ascending: false });
    if (fkField === "branch_id") q = q.eq("branch_id", parentId);
    else if (fkField === "batch_id") q = q.eq("batch_id", parentId);
    else q = q.eq("association_id", parentId);
    const { data } = await q;
    setProjects((data as ProjectRow[]) ?? []);
  }, [parentId, fkField, kind]);

  const loadDirectory = useCallback(async () => {
    if (!siteWide) return;
    setDirectoryLoading(true);
    setDirectoryError(null);
    try {
      let q = supabase
        .from("projects")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(250);

      if (dirOwner === "oba") {
        q = q.is("branch_id", null).is("batch_id", null).is("association_id", null);
      } else if (dirOwner === "branch") {
        if (dirParentId) q = q.eq("branch_id", dirParentId);
        else q = q.not("branch_id", "is", null);
      } else if (dirOwner === "batch") {
        if (dirParentId) q = q.eq("batch_id", dirParentId);
        else q = q.not("batch_id", "is", null);
      } else if (dirOwner === "association") {
        if (dirParentId) q = q.eq("association_id", dirParentId);
        else q = q.not("association_id", "is", null);
      }

      const term = dirSearch.trim();
      if (term) {
        const pat = `%${escapeIlikePattern(term)}%`;
        q = q.or(`title.ilike.${pat},description.ilike.${pat}`);
      }

      const { data, error } = await q;
      if (error) throw error;
      setDirectoryProjects((data as ProjectRow[]) ?? []);
    } catch (err) {
      console.error(err);
      setDirectoryError(postgrestErrorMessage(err));
      setDirectoryProjects([]);
    } finally {
      setDirectoryLoading(false);
    }
  }, [siteWide, dirOwner, dirParentId, dirSearch]);

  useEffect(() => {
    void loadParents();
  }, [loadParents]);

  useEffect(() => {
    if (siteWide) return;
    void loadProjects();
  }, [loadProjects, siteWide]);

  useEffect(() => {
    if (!siteWide) return;
    void loadDirectory();
  }, [loadDirectory, siteWide]);

  useEffect(() => {
    if (!siteWide) {
      setDirParents([]);
      return;
    }
    if (dirOwner !== "branch" && dirOwner !== "batch" && dirOwner !== "association") {
      setDirParentId("");
      setDirParents([]);
      return;
    }
    void (async () => {
      if (dirOwner === "branch") {
        const { data } = await supabase.from("branches").select("id,name").order("name");
        setDirParents(
          (data ?? []).map((r: { id: string; name: string }) => ({
            id: r.id,
            label: r.name,
          }))
        );
      } else if (dirOwner === "batch") {
        const { data } = await supabase
          .from("batches")
          .select("id,custom_title,official_title,slug")
          .order("al_year", { ascending: false, nullsFirst: false });
        setDirParents(
          (data ?? []).map(
            (r: { id: string; custom_title: string | null; official_title: string | null; slug: string }) => ({
              id: r.id,
              label: r.custom_title || r.official_title || r.slug,
            })
          )
        );
      } else {
        const { data } = await supabase.from("associations").select("id,name").order("name");
        setDirParents(
          (data ?? []).map((r: { id: string; name: string }) => ({
            id: r.id,
            label: r.name,
          }))
        );
      }
    })();
  }, [dirOwner, siteWide]);

  function projectOwnerLabel(p: ProjectRow) {
    if (!p.branch_id && !p.batch_id && !p.association_id) return "OBA";
    if (p.branch_id) return "Branch";
    if (p.batch_id) return "Batch";
    return "Association";
  }

  function startNew() {
    setEditingId(null);
    setTitle("");
    setDescription("");
    setLinkUrl("");
    setImageUrl("");
    setFile(null);
    setFormNotice(null);
  }

  function startEdit(p: ProjectRow) {
    setEditingId(p.id);
    setTitle(p.title);
    setDescription(p.description ?? "");
    setLinkUrl(p.link_url ?? "");
    setImageUrl(p.image_url ?? "");
    setFile(null);
    setFormNotice(null);
    if (siteWide) {
      if (!p.branch_id && !p.batch_id && !p.association_id) {
        setKind("oba");
        setParentId("");
      } else if (p.branch_id) {
        setKind("branch");
        setParentId(p.branch_id);
      } else if (p.batch_id) {
        setKind("batch");
        setParentId(p.batch_id);
      } else if (p.association_id) {
        setKind("association");
        setParentId(p.association_id);
      }
    }
  }

  function clearFormNoticeSoon() {
    window.setTimeout(() => {
      setFormNotice((n) => (n?.kind === "success" ? null : n));
    }, 4500);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setFormNotice(null);
    setListNotice(null);
    if (kind !== "oba" && !parentId) {
      setFormNotice({ kind: "error", text: "Select a parent (branch, batch, or association)." });
      return;
    }
    if (kind === "oba" && !siteWide) {
      setFormNotice({
        kind: "error",
        text: "Only portal managers or site-wide admins can manage OBA-level projects.",
      });
      return;
    }
    setSaving(true);
    try {
      let url = imageUrl.trim();
      if (file) url = await uploadPublicImage(file, "projects");
      const base = {
        title: title.trim(),
        description: description || null,
        link_url: linkUrl || null,
        image_url: url || null,
        branch_id: kind === "branch" ? parentId : null,
        batch_id: kind === "batch" ? parentId : null,
        association_id: kind === "association" ? parentId : null,
      };
      if (!base.title) {
        setFormNotice({ kind: "error", text: "Title is required." });
        return;
      }
      if (editingId) {
        const { error } = await supabase.from("projects").update(base).eq("id", editingId);
        if (error) throw error;
        setFormNotice({ kind: "success", text: "Project saved successfully." });
        clearFormNoticeSoon();
      } else {
        const { error } = await supabase.from("projects").insert(base);
        if (error) throw error;
        setFormNotice({ kind: "success", text: "Project created successfully." });
        clearFormNoticeSoon();
      }
      setFile(null);
      if (!siteWide) await loadProjects();
      else await loadDirectory();
      startNew();
    } catch (err) {
      console.error(err);
      setFormNotice({
        kind: "error",
        text: postgrestErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Delete this project? This cannot be undone.")) return;
    setListNotice(null);
    setDeletingId(id);
    try {
      const { error } = await supabase.from("projects").delete().eq("id", id);
      if (error) throw error;
      setListNotice({ kind: "success", text: "Project deleted successfully." });
      window.setTimeout(() => setListNotice((n) => (n?.kind === "success" ? null : n)), 4000);
      if (!siteWide) await loadProjects();
      else await loadDirectory();
      if (editingId === id) startNew();
    } catch (err) {
      console.error(err);
      setListNotice({
        kind: "error",
        text: `Could not delete: ${postgrestErrorMessage(err)}`,
      });
    } finally {
      setDeletingId(null);
    }
  }

  const noticeClass = (k: "success" | "error") =>
    k === "success"
      ? "rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
      : "rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900";

  const listRows = siteWide ? directoryProjects : projects;
  const showProjectList = !siteWide || (!directoryLoading && !directoryError);

  const parentRow = (
    <div className="flex flex-wrap gap-4">
      <label className="text-sm font-semibold text-brand-blue">
        Parent type
        <select
          value={kind}
          onChange={(e) => {
            const v = e.target.value as ParentKind;
            setKind(v);
            if (v === "oba") setParentId("");
          }}
          disabled={!siteWide}
          className="ml-2 rounded border border-brand-gray px-2 py-1 disabled:opacity-60"
        >
          <option value="branch">Branch</option>
          <option value="batch">Batch</option>
          <option value="association">Association</option>
          {siteWide ? <option value="oba">OBA (site-wide)</option> : null}
        </select>
      </label>
      {kind === "oba" ? (
        <p className="self-end text-sm text-brand-blue/75">
          No branch, batch, or association — visible on the public Projects page as{" "}
          <span className="font-semibold text-brand-blue">OBA</span>.
        </p>
      ) : (
        <label className="text-sm font-semibold text-brand-blue">
          Parent
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            disabled={!siteWide}
            className="ml-2 max-w-xs rounded border border-brand-gray px-2 py-1 disabled:opacity-60"
          >
            <option value="">— Select —</option>
            {parents.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );

  const projectForm = (
    <form onSubmit={onSave} className="mt-5 space-y-3" aria-busy={saving}>
      {formNotice ? (
        <div role="status" className={noticeClass(formNotice.kind)}>
          {formNotice.text}
        </div>
      ) : null}
      <input
        required
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
      />
      <textarea
        placeholder="Description"
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
      />
      <input
        placeholder="Link URL"
        value={linkUrl}
        onChange={(e) => setLinkUrl(e.target.value)}
        className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
      />
      <p className="text-xs text-brand-blue/60">
        Public lists show newest updates first (last saved appears at the top).
      </p>
      <AdminImagePicker
        label="Image"
        inputId="project-image"
        remoteUrl={imageUrl}
        selectedFile={file}
        onFileChange={setFile}
        onClear={() => {
          setFile(null);
          setImageUrl("");
        }}
        disabled={saving}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-full bg-brand-maroon px-6 py-2 text-sm font-bold uppercase text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          onClick={startNew}
          disabled={saving}
          className="rounded-full border border-brand-gray px-4 py-2 text-sm disabled:opacity-60"
        >
          Clear
        </button>
      </div>
    </form>
  );

  const directoryFilters = siteWide ? (
    <div className="mt-4 rounded-lg border border-brand-gray/80 bg-brand-gray/20 p-3">
      <p className="text-sm font-semibold text-brand-blue">Find projects to edit or delete</p>
      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="text-sm font-semibold text-brand-blue">
          Scope
          <select
            value={dirOwner}
            onChange={(e) => {
              setDirOwner(e.target.value as DirOwner);
              setDirParentId("");
            }}
            className="ml-2 rounded border border-brand-gray bg-white px-2 py-1.5"
          >
            <option value="all">All</option>
            <option value="oba">OBA only</option>
            <option value="branch">Branch</option>
            <option value="batch">Batch</option>
            <option value="association">Association</option>
          </select>
        </label>
        {dirOwner === "branch" || dirOwner === "batch" || dirOwner === "association" ? (
          <label className="text-sm font-semibold text-brand-blue">
            {dirOwner === "branch" ? "Branch" : dirOwner === "batch" ? "Batch" : "Association"}{" "}
            <span className="font-normal text-brand-blue/60">(optional)</span>
            <select
              value={dirParentId}
              onChange={(e) => setDirParentId(e.target.value)}
              className="ml-2 max-w-xs rounded border border-brand-gray bg-white px-2 py-1.5"
            >
              <option value="">Any</option>
              {dirParents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="min-w-[12rem] flex-1 text-sm font-semibold text-brand-blue">
          Search
          <input
            type="search"
            value={dirSearch}
            onChange={(e) => setDirSearch(e.target.value)}
            placeholder="Title or description…"
            className="mt-1 block w-full rounded border border-brand-gray bg-white px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={() => void loadDirectory()}
          className="rounded-full border border-brand-gray bg-white px-4 py-2 text-sm font-semibold text-brand-blue hover:bg-brand-gray/40"
        >
          Refresh list
        </button>
      </div>
      <p className="mt-2 text-xs text-brand-blue/70">
        Up to 250 results per search. Use scope and search together to narrow results.
      </p>
    </div>
  ) : null;

  const projectListBody = (
    <>
      {listNotice ? (
        <div className="mt-2" role="status">
          <div className={noticeClass(listNotice.kind)}>{listNotice.text}</div>
        </div>
      ) : null}
      {siteWide && directoryLoading ? (
        <p className="mt-4 text-sm text-brand-blue/60">Loading projects…</p>
      ) : null}
      {siteWide && directoryError ? <p className="mt-4 text-sm text-red-800">{directoryError}</p> : null}
      {showProjectList ? (
        <div className="mt-4 max-h-[min(70vh,36rem)] overflow-auto rounded-lg border border-brand-gray/80 bg-brand-gray/30 p-2">
          <ul className="space-y-1">
          {listRows.length === 0 ? (
            <li className="px-2 py-3 text-sm text-brand-blue/60">No projects match the current filters.</li>
          ) : null}
          {listRows.map((p) => {
            const selected = editingId === p.id;
            return (
              <li
                key={p.id}
                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-3 transition-colors ${adminListRowHighlightClass(selected)}`}
              >
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-brand-blue">{p.title}</span>
                  <span className="ml-2 text-xs font-semibold uppercase text-brand-maroon/90">
                    {projectOwnerLabel(p)}
                  </span>
                </div>
                <div className="flex shrink-0 flex-row items-center gap-4">
                  <button
                    type="button"
                    className="text-xs font-bold uppercase tracking-wide text-brand-maroon disabled:opacity-50"
                    disabled={deletingId === p.id}
                    onClick={() => startEdit(p)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-xs font-bold uppercase tracking-wide text-red-700 disabled:opacity-50"
                    disabled={deletingId === p.id}
                    onClick={() => void onDelete(p.id)}
                  >
                    {deletingId === p.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </li>
            );
          })}
          </ul>
        </div>
      ) : null}
    </>
  );

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-brand-blue">Projects</h2>
      <p className="mt-2 text-sm text-brand-blue/80">
        Link projects to a branch, batch, association, or{" "}
        <strong className="font-semibold">OBA</strong> (site-wide, no sub-unit).
      </p>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
        <section
          aria-labelledby="projects-list-heading"
          className={adminListSectionClass}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-gray pb-3">
            <h3 id="projects-list-heading" className="font-display text-lg font-bold text-brand-blue">
              {siteWide ? "Project directory" : "Your projects"}
            </h3>
            <button
              type="button"
              onClick={() => {
                if (!saving && editingId) startNew();
              }}
              disabled={saving || !editingId}
              title={
                !editingId
                  ? "You are already adding a new project — use the form on the right"
                  : "Clear the editor and add a new project"
              }
              className={adminAddNewSecondaryButtonClass}
            >
              Add new project
            </button>
          </div>
          {directoryFilters}
          {projectListBody}
        </section>
        <section
          aria-labelledby="projects-form-heading"
          className={adminFormSectionClass}
        >
          <div className="border-b border-brand-gray pb-3">
            <h3 id="projects-form-heading" className="font-display text-lg font-bold text-brand-blue">
              {editingId ? "Edit project" : "Add new project"}
            </h3>
            {editingId && title.trim() ? (
              <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">&ldquo;{title.trim()}&rdquo;</p>
            ) : null}
          </div>
          <div className="mt-4 space-y-4">
            {parentRow}
            {lockedKind && !siteWide ? (
              <p className="text-xs text-brand-blue/70">
                Projects are linked to your assigned {lockedKind.replace("_", " ")} only.
              </p>
            ) : null}
            {projectForm}
          </div>
        </section>
      </div>
    </div>
  );
}
